import { Alert, Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import MicNoneRoundedIcon from '@mui/icons-material/MicNoneRounded';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import PhoneDisabledRoundedIcon from '@mui/icons-material/PhoneDisabledRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import TextsmsOutlinedIcon from '@mui/icons-material/TextsmsOutlined';
import { LiveAiVoiceController, LiveAiVoiceState } from '../../hooks/useLiveAiVoice';

interface LiveAiVoiceControlsProps {
    voice: LiveAiVoiceController;
    disabled?: boolean;
}

const labels: Record<LiveAiVoiceState, string> = {
    idle: 'Voice ready',
    'requesting-permission': 'Waiting for microphone permission',
    connecting: 'Connecting',
    listening: 'Listening',
    thinking: 'Thinking',
    speaking: 'Speaking',
    muted: 'Microphone muted',
    reconnecting: 'Reconnecting',
    ended: 'Voice ended',
    error: 'Voice disconnected',
    unavailable: 'Voice unavailable',
};

const busyStates: LiveAiVoiceState[] = ['requesting-permission', 'connecting', 'reconnecting'];
const connectedStates: LiveAiVoiceState[] = ['listening', 'thinking', 'speaking', 'muted'];

const LiveAiVoiceControls = ({ voice, disabled = false }: LiveAiVoiceControlsProps) => {
    const connected = connectedStates.includes(voice.state);
    const canReconnect = voice.state === 'error' || voice.state === 'ended';
    const active = connected || busyStates.includes(voice.state);

    const controlSx = {
        width: 38,
        height: 38,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
    } as const;

    return (
        <Stack spacing={1} data-testid="live-ai-voice-controls" sx={{ flex: '0 0 auto', maxWidth: '100%' }}>
            <Stack direction="row" spacing={0.75} alignItems="center" minHeight={38}>
                {!connected && !busyStates.includes(voice.state) && voice.state !== 'unavailable' && (
                    <Tooltip title={canReconnect ? 'Reconnect voice' : 'Start voice conversation'}>
                        <span>
                            <IconButton
                                aria-label={canReconnect ? 'Reconnect voice' : 'Start voice conversation'}
                                color="primary"
                                disabled={disabled}
                                onClick={() => void (canReconnect ? voice.reconnect() : voice.start())}
                                sx={{ ...controlSx, borderColor: 'primary.main' }}
                            >
                                {canReconnect ? <RefreshRoundedIcon /> : <GraphicEqRoundedIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {busyStates.includes(voice.state) && (
                    <Tooltip title={labels[voice.state]}>
                        <Box aria-label={labels[voice.state]} sx={{ ...controlSx, display: 'grid', placeItems: 'center', borderRadius: '50%' }}>
                            <CircularProgress size={20} />
                        </Box>
                    </Tooltip>
                )}
                {connected && (
                    <Box
                        aria-hidden="true"
                        sx={{
                            ...controlSx,
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: '50%',
                            borderColor: voice.isMuted ? 'divider' : 'primary.main',
                            bgcolor: voice.isMuted ? 'action.hover' : 'primary.main',
                            color: voice.isMuted ? 'text.secondary' : 'primary.contrastText',
                            ...(!voice.isMuted && {
                                animation: 'voicePulse 1.3s ease-in-out infinite',
                                '@keyframes voicePulse': { '50%': { opacity: 0.6 } },
                            }),
                        }}
                    >
                        {voice.isMuted ? <MicOffRoundedIcon fontSize="small" /> : <GraphicEqRoundedIcon fontSize="small" />}
                    </Box>
                )}
                {active && (
                    <Typography
                        variant="caption"
                        color={voice.isMuted ? 'text.secondary' : 'text.primary'}
                        aria-live="polite"
                        data-testid={connected ? 'active-microphone-indicator' : undefined}
                        sx={{ mr: 0.25, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}
                    >
                        {labels[voice.state]}
                    </Typography>
                )}
                {connected && (
                    <Tooltip title={voice.isMuted ? 'Unmute' : 'Mute'}>
                        <IconButton aria-label={voice.isMuted ? 'Unmute' : 'Mute'} onClick={voice.toggleMute} sx={controlSx}>
                            {voice.isMuted ? <MicNoneRoundedIcon /> : <MicOffRoundedIcon />}
                        </IconButton>
                    </Tooltip>
                )}
                {connected && (
                    <Tooltip title="End voice conversation">
                        <IconButton aria-label="End" color="error" onClick={voice.end} sx={controlSx}>
                            <PhoneDisabledRoundedIcon />
                        </IconButton>
                    </Tooltip>
                )}
                {(active || voice.state === 'error' || voice.state === 'unavailable') && (
                    <Tooltip title="Use text instead">
                        <IconButton aria-label="Use text instead" onClick={voice.fallbackToText} sx={{ ...controlSx, ml: 'auto !important' }}>
                            <TextsmsOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {(voice.userCaption || voice.assistantCaption) && (
                <Box aria-live="polite" aria-label="Live voice captions" sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                    {voice.userCaption && <Typography variant="caption" component="div"><strong>You:</strong> {voice.userCaption}</Typography>}
                    {voice.assistantCaption && <Typography variant="caption" component="div"><strong>Synergist AI:</strong> {voice.assistantCaption}</Typography>}
                </Box>
            )}

            {voice.error && <Alert severity={voice.state === 'unavailable' ? 'info' : 'warning'} sx={{ py: 0 }}>{voice.error}</Alert>}
        </Stack>
    );
};

export default LiveAiVoiceControls;
